import React, { SetStateAction, useEffect, useState } from "react";
import { endpoints } from "config";
import api, { ErrorMessage } from "api";
import { AxiosResponse } from "axios";
import sitesIcon from "assets/Icon_Menu_Blue_03.png";
import projectSetupIcon from "assets/Icon_Menu_Blue_02.png";
import _ from "lodash";
import { Spinner } from "components/Spinner/Spinner";
import { customField } from "components/helpers/CustomFieldRenderers/CustomFormFields/model";
import { customFieldRender } from "./GatewaySummary";

interface Details {
  gateways: {}[];
  newBrand: string;
  projectId: string;
  projectManager: string;
  projectName: string;
  projectReference: string;
  projectStatus: string;
  customFields: customField[];
  sitesCustomFields: customField[];
  siteAddress: string;
  siteStatus: string;
  region: string;
  sourcedFrom: string;
  spocEmail: string;
  spocName: string;
  spocPhone: string;
  type: string;

  siteId: string | null;
  siteName: string | null;
  siteStatue: string | null;
}

type InitialDataResponse = {
  id: number;
  details: Details;
  metadata: {}[];
  status: "0" | "1";
  errors: ErrorMessage[];
};

export const fetchData = async (
  endpoint: string,
  Id: string | number,
  setData: React.SetStateAction<any>,
  setMetaData: React.SetStateAction<any>,
  setLoading: React.Dispatch<SetStateAction<boolean>>,
  setError: React.Dispatch<SetStateAction<boolean>>,
  backgroundReload: boolean = false,
  initialFetch?: boolean,
  setFetchedInitialData?: React.Dispatch<SetStateAction<boolean>>
) => {
  if (!backgroundReload) {
    setLoading(true);
  }

  if (endpoint) {
    try {
      const response: AxiosResponse<InitialDataResponse, any> = await api.get(endpoint, {
        params: { Id: Id },
      });
      if (response && response.data && response.data.status === "1") {
        setData(response.data.details);
        if (setMetaData) {
          setMetaData(response.data.metadata);
        }
      } else {
        // if (!backgroundReload) {
        setError(true);
        // }
      }
    } catch (error) {
      console.log(error);

      if (!backgroundReload) {
        setLoading(false);
      }
      setError(true);
      // throw Error;
    }
  }
  if (!backgroundReload) {
    setLoading(false);
  }

  if (initialFetch && setFetchedInitialData) {
    setFetchedInitialData(true);
  }
};

type Props = {
  selectedId: string | number;
};

export const SummaryTable: React.FC<Props> = ({ selectedId }) => {
  const [data, setData] = useState<Details | null>(null);
  const [metaData, setMetaData] = useState<any | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    fetchData(
      endpoints.projectmanagement.overviewSummary,
      selectedId,
      setData,
      setMetaData,
      setLoading,
      setError
    );
  }, []);

  const groupedMetaData = _.groupBy(metaData, "groupLabel");
  delete groupedMetaData["Details"];
  delete groupedMetaData["null"];

  const hasCustomFields = (key: keyof Details) => {
    if (data) {
      return Array.isArray(data[key]) && (data[key]?.length ?? 0) > 0;
    } else {
      return false;
    }
  };

  const renderCustomFields = (key: string) => {
    const ref: { [key: string]: any } = {
      ["Project Details"]: {
        customFieldGroup: "Additional Project Info",
        dataSource: "customFields",
      },
      ["Site Details"]: {
        customFieldGroup: "Additional Site Info",
        dataSource: "sitesCustomFields",
      },
    };
    if (!ref[key]) return;
    return (
      hasCustomFields(ref[key].dataSource) && (
        <>
          {groupedMetaData[ref[key].customFieldGroup].map((field, _i) => (
            <React.Fragment key={`${field.label.toLowerCase()}-${_i}`}>
              {(
                data && (data[_.lowerFirst(field?.name) as keyof typeof data] as customField[])
              )?.map((customField: customField, idx) => {
                return (
                  <React.Fragment key={`${customField.name.toLowerCase()}-${_i}`}>
                    <div className="TableLabel">{customField.name}</div>
                    <div className="TableValue">{customFieldRender(customField, true)}</div>
                  </React.Fragment>
                );
              })}
            </React.Fragment>
          ))}
        </>
      )
    );
  };

  return (
    <div className="SummaryTableContainer">
      {!loading && data && (
        <>
          {Object.keys(groupedMetaData).map((key, _i) => {
            return (
              <React.Fragment key={`fragment-container-${_i}`}>
                <div
                  className="ProjectSummary"
                  style={bCustomFieldKey(key) ? { display: "none" } : {}}
                >
                  <div className="TableHeading" style={{ paddingLeft: "5px" }}>
                    <img
                      className="Icon"
                      src={String(key).includes("Site") ? sitesIcon : projectSetupIcon}
                    />
                    <h4>{key.toUpperCase()}</h4>
                  </div>
                  <div className="SummaryTable">
                    {groupedMetaData[key].map((field, _i) => {
                      return bCustomFieldKey(key) ? (
                        <React.Fragment key={`custom-field-${_i}`} />
                      ) : (
                        <React.Fragment key={`${field.label.toLowerCase()}-${_i}`}>
                          <div className="TableLabel">{field.label}</div>
                          <div className="TableValue">
                            {data[_.lowerFirst(field?.name) as keyof typeof data]}
                          </div>
                        </React.Fragment>
                      );
                    })}
                    {renderCustomFields(key)}
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </>
      )}

      {!loading && !data && !error && (
        <div className="NoData">
          <div className="Text">No gateway data</div>
        </div>
      )}

      {error && (
        <div className="Error">
          <div className="Title">Error</div>

          <div className="Text">Something went wrong, please try again later</div>
        </div>
      )}

      {loading && <Spinner />}
    </div>
  );
};

const bCustomFieldKey = (key: string) =>
  key === "Additional Site Info" || key === "Additional Project Info";
